import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { walletAPI, premiumAPI, resolveImageUrl } from '@/lib/api';
import api from '@/lib/api';
import { toast } from 'sonner';
import { 
  Wallet, 
  TrendingUp, 
  ArrowUpRight, 
  ArrowDownRight,
  Lock,
  Unlock,
  Clock,
  CheckCircle,
  AlertCircle,
  Coins,
  PiggyBank,
  Send,
  History,
  Sparkles,
  Users,
  Copy,
  Share2,
  Crown,
  Link2,
  ExternalLink,
  Loader2,
  Unlink,
  Globe
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { BrowserProvider, Contract, formatUnits } from 'ethers';

const AWA_CONTRACT = '0x76b7a4dfef135ddb9519c698768704a78b670e07';
const POLYGON_CHAIN_ID = '0x89'; // 137
const ERC20_ABI = [
  'function balanceOf(address) view returns (uint256)',
  'function symbol() view returns (string)',
  'function decimals() view returns (uint8)',
];

/* ═══ On-Chain Wallet Section ═══ */
function OnChainWalletSection() {
  const [walletAddress, setWalletAddress] = useState(null);
  const [awaBalance, setAwaBalance] = useState(null);
  const [chainOk, setChainOk] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [reading, setReading] = useState(false);
  const [linked, setLinked] = useState(null); // {address, status} or null
  const [linking, setLinking] = useState(false);

  // Check linked wallet on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/wallet-link/status');
        if (res.data.linked) setLinked(res.data);
      } catch {}
    })();
  }, []);

  const getProvider = () => {
    if (window.ethereum) return new BrowserProvider(window.ethereum);
    return null;
  };

  const connectWallet = async () => {
    setConnecting(true);
    try {
      const provider = getProvider();
      if (!provider) { toast.error('Instale MetaMask ou Coinbase Wallet'); return; }
      
      const accounts = await provider.send('eth_requestAccounts', []);
      const addr = accounts[0];
      setWalletAddress(addr);
      
      // Check chain
      const network = await provider.getNetwork();
      const isPolygon = network.chainId === 137n;
      setChainOk(isPolygon);
      
      if (!isPolygon) {
        try {
          await provider.send('wallet_switchEthereumChain', [{ chainId: POLYGON_CHAIN_ID }]);
          setChainOk(true);
        } catch (e) {
          if (e.code === 4902) {
            await provider.send('wallet_addEthereumChain', [{
              chainId: POLYGON_CHAIN_ID,
              chainName: 'Polygon Mainnet',
              rpcUrls: ['https://polygon-rpc.com'],
              nativeCurrency: { name: 'MATIC', symbol: 'MATIC', decimals: 18 },
              blockExplorerUrls: ['https://polygonscan.com'],
            }]);
            setChainOk(true);
          } else {
            toast.error('Troque para a rede Polygon Mainnet na sua carteira');
          }
        }
      }
      
      // Read AWA balance
      await readBalance(addr, provider);
    } catch (e) {
      toast.error(e.message?.includes('rejected') ? 'Conexão cancelada' : 'Erro ao conectar carteira');
    } finally { setConnecting(false); }
  };

  const readBalance = async (addr, providerArg) => {
    setReading(true);
    try {
      const provider = providerArg || getProvider();
      if (!provider) return;
      const contract = new Contract(AWA_CONTRACT, ERC20_ABI, provider);
      const bal = await contract.balanceOf(addr);
      const decimals = await contract.decimals();
      setAwaBalance(formatUnits(bal, decimals));
    } catch (e) {
      setAwaBalance('—');
    } finally { setReading(false); }
  };

  const linkWallet = async () => {
    if (!walletAddress) return;
    setLinking(true);
    try {
      // Get nonce from backend
      const nonceRes = await api.get('/wallet-link/nonce');
      const { nonce, message } = nonceRes.data;

      // Sign message
      const provider = getProvider();
      const signer = await provider.getSigner();
      const signature = await signer.signMessage(message);

      // Verify on backend
      const verifyRes = await api.post('/wallet-link/verify', {
        address: walletAddress,
        signature,
        nonce,
      });
      
      if (verifyRes.data.success) {
        setLinked({ address: walletAddress, status: 'verified' });
        toast.success('Carteira vinculada com sucesso!');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao vincular carteira');
    } finally { setLinking(false); }
  };

  const unlinkWallet = async () => {
    try {
      await api.delete('/wallet-link/unlink');
      setLinked(null);
      toast.success('Carteira desvinculada');
    } catch { toast.error('Erro ao desvincular'); }
  };

  const disconnect = () => {
    setWalletAddress(null);
    setAwaBalance(null);
    setChainOk(false);
  };

  const shortAddr = (addr) => addr ? `${addr.slice(0, 6)}...${addr.slice(-4)}` : '';

  return (
    <Card className="rounded-2xl border-border/40 shadow-sm mb-6 overflow-hidden" data-testid="onchain-wallet-section">
      {/* Header bar */}
      <div className="px-5 py-3 bg-gradient-to-r from-violet-600 to-purple-600 flex items-center justify-between">
        <div className="flex items-center gap-2 text-white">
          <Globe className="h-4 w-4" />
          <span className="font-semibold text-sm">AWA On-chain (Polygon)</span>
        </div>
        {walletAddress && (
          <Badge className="bg-white/20 text-white text-[10px]">{shortAddr(walletAddress)}</Badge>
        )}
      </div>
      
      <CardContent className="p-5">
        {!walletAddress ? (
          <div className="text-center py-4">
            <p className="text-sm text-muted-foreground mb-4">Conecte sua carteira para ver seu saldo AWA na Polygon</p>
            <Button onClick={connectWallet} disabled={connecting} className="rounded-xl gap-2" data-testid="connect-wallet-btn">
              {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
              Conectar Carteira
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Balance */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Saldo AWA On-chain</p>
                <p className="text-2xl font-bold" data-testid="onchain-balance">
                  {reading ? <Loader2 className="h-5 w-5 animate-spin inline" /> : awaBalance !== null ? `${Number(awaBalance).toLocaleString('pt-BR')} AWA` : '—'}
                </p>
              </div>
              <div className="flex items-center gap-1.5">
                {chainOk ? (
                  <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-[10px]">
                    <CheckCircle className="h-3 w-3 mr-0.5" />Polygon
                  </Badge>
                ) : (
                  <Badge className="bg-amber-100 text-amber-700 text-[10px]">
                    <AlertCircle className="h-3 w-3 mr-0.5" />Rede incorreta
                  </Badge>
                )}
              </div>
            </div>

            {/* Link status */}
            <div className="flex items-center gap-2 flex-wrap">
              {linked ? (
                <div className="flex items-center gap-2 text-xs">
                  <Badge className="bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400 text-[10px]">
                    <CheckCircle className="h-3 w-3 mr-0.5" />Verificada
                  </Badge>
                  <span className="text-muted-foreground">{shortAddr(linked.address)}</span>
                  <button onClick={unlinkWallet} className="text-red-500 hover:text-red-600 text-[10px] underline" data-testid="unlink-wallet-btn">
                    Desvincular
                  </button>
                </div>
              ) : (
                <Button onClick={linkWallet} disabled={linking} variant="outline" size="sm" className="rounded-xl text-xs gap-1.5" data-testid="link-wallet-btn">
                  {linking ? <Loader2 className="h-3 w-3 animate-spin" /> : <Lock className="h-3 w-3" />}
                  Vincular Carteira
                </Button>
              )}
              <button onClick={disconnect} className="text-muted-foreground hover:text-foreground text-[10px] underline ml-auto" data-testid="disconnect-wallet-btn">
                Desconectar
              </button>
            </div>

            {/* Polygonscan link */}
            <a
              href={`https://polygonscan.com/token/${AWA_CONTRACT}?a=${walletAddress}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-[10px] text-violet-500 hover:text-violet-600"
            >
              <ExternalLink className="h-3 w-3" />Ver no Polygonscan
            </a>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function WalletPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [stakingPositions, setStakingPositions] = useState([]);
  const [withdrawRequests, setWithdrawRequests] = useState([]);
  const [affiliateStats, setAffiliateStats] = useState(null);
  const [affiliateReferrals, setAffiliateReferrals] = useState([]);
  
  // Modais
  const [showStakingModal, setShowStakingModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [stakingAmount, setStakingAmount] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [pixKey, setPixKey] = useState('');
  const [pixKeyType, setPixKeyType] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadWalletData();
  }, []);

  const loadWalletData = async () => {
    setLoading(true);
    try {
      const [balanceRes, transRes, stakingRes, withdrawRes, affiliateStatsRes, affiliateRefRes] = await Promise.all([
        walletAPI.getBalance(),
        walletAPI.getTransactions({ limit: 20 }),
        walletAPI.getStakingPositions(),
        walletAPI.getWithdrawRequests(),
        premiumAPI.getAffiliateStats().catch(() => ({ data: null })),
        premiumAPI.getAffiliateReferrals().catch(() => ({ data: { referrals: [] } }))
      ]);
      
      setBalance(balanceRes.data);
      setTransactions(transRes.data.transactions || []);
      setStakingPositions(stakingRes.data.positions || []);
      setWithdrawRequests(withdrawRes.data.requests || []);
      setAffiliateStats(affiliateStatsRes.data);
      setAffiliateReferrals(affiliateRefRes.data?.referrals || []);
    } catch (error) {
      console.error('Failed to load wallet data:', error);
      toast.error('Erro ao carregar dados da carteira');
    } finally {
      setLoading(false);
    }
  };

  const handleStaking = async () => {
    if (!stakingAmount || parseFloat(stakingAmount) <= 0) {
      toast.error('Informe um valor válido');
      return;
    }

    setActionLoading(true);
    try {
      const response = await walletAPI.createStaking(parseFloat(stakingAmount));
      toast.success(response.data.message);
      setShowStakingModal(false);
      setStakingAmount('');
      loadWalletData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar staking');
    } finally {
      setActionLoading(false);
    }
  };

  const handleWithdraw = async () => {
    if (!withdrawAmount || parseFloat(withdrawAmount) < 1000) {
      toast.error('Valor mínimo para saque: 1000 AWA');
      return;
    }
    if (!pixKey || !pixKeyType) {
      toast.error('Informe a chave PIX');
      return;
    }

    setActionLoading(true);
    try {
      const response = await walletAPI.requestWithdraw({
        amount_awa: parseFloat(withdrawAmount),
        pix_key: pixKey,
        pix_key_type: pixKeyType
      });
      toast.success(response.data.message);
      setShowWithdrawModal(false);
      setWithdrawAmount('');
      setPixKey('');
      setPixKeyType('');
      loadWalletData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao solicitar saque');
    } finally {
      setActionLoading(false);
    }
  };

  const copyReferralLink = () => {
    if (affiliateStats?.referral_link) {
      navigator.clipboard.writeText(affiliateStats.referral_link);
      toast.success('Link copiado!');
    }
  };

  const shareReferralLink = async () => {
    if (affiliateStats?.referral_link && navigator.share) {
      try {
        await navigator.share({
          title: 'U-Clean - Convite',
          text: 'Junte-se à comunidade U-Clean de apoio à recuperação!',
          url: affiliateStats.referral_link
        });
      } catch (e) {
        copyReferralLink();
      }
    } else {
      copyReferralLink();
    }
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'checkin': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'task': return <Sparkles className="h-4 w-4 text-blue-500" />;
      case 'referral': return <TrendingUp className="h-4 w-4 text-purple-500" />;
      case 'staking': return <Lock className="h-4 w-4 text-amber-500" />;
      case 'staking_lock': return <Lock className="h-4 w-4 text-amber-500" />;
      case 'staking_unlock': return <Unlock className="h-4 w-4 text-green-500" />;
      case 'withdraw_request': return <Send className="h-4 w-4 text-red-500" />;
      case 'withdraw_paid': return <CheckCircle className="h-4 w-4 text-green-500" />;
      default: return <Coins className="h-4 w-4 text-gray-500" />;
    }
  };

  const getWithdrawStatusBadge = (status) => {
    switch (status) {
      case 'pending': return <Badge variant="outline" className="text-yellow-600">Pendente</Badge>;
      case 'approved': return <Badge variant="outline" className="text-blue-600">Aprovado</Badge>;
      case 'paid': return <Badge className="bg-green-500">Pago</Badge>;
      case 'rejected': return <Badge variant="destructive">Rejeitado</Badge>;
      case 'cancelled': return <Badge variant="secondary">Cancelado</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto py-6 px-4 space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid md:grid-cols-3 gap-4">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto" data-testid="wallet-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Wallet className="h-6 w-6 text-violet-600" strokeWidth={1.5} />
            Carteira VSA
          </h1>
          <p className="text-sm text-muted-foreground">Sua carteira AWARE-AWA</p>
        </div>
        <Badge variant="outline" className="text-xs rounded-md">
          R$ 1,00 = 1.000 AWA
        </Badge>
      </div>

      {/* On-Chain Wallet Section */}
      <OnChainWalletSection />

      {/* Saldo Interno U-Clean */}
      <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Saldo Interno U-Clean</h2>

      {/* Saldos */}
      <div className="grid md:grid-cols-4 gap-4 mb-6">
        {/* Saldo Total */}
        <Card className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-purple-100">Saldo Total</span>
              <Wallet className="h-5 w-5 text-purple-200" />
            </div>
            <div className="text-3xl font-bold">{balance?.total_display?.toFixed(2)} AWA</div>
            <div className="text-sm text-purple-200">≈ R$ {balance?.total_brl_display?.toFixed(2)}</div>
          </CardContent>
        </Card>

        {/* AWA Comum */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-muted-foreground">AWA Comum</span>
              <Coins className="h-5 w-5 text-blue-500" />
            </div>
            <div className="text-2xl font-bold">{balance?.common_display?.toFixed(2)}</div>
            <div className="text-xs text-muted-foreground">Gamificação (não sacável)</div>
          </CardContent>
        </Card>

        {/* AWA Sacável */}
        <Card className="border-green-200 bg-green-50/50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-muted-foreground">AWA Sacável</span>
              <TrendingUp className="h-5 w-5 text-green-500" />
            </div>
            <div className="text-2xl font-bold text-green-600">{balance?.withdrawable_display?.toFixed(2)}</div>
            <div className="text-xs text-muted-foreground">Comissões de afiliado</div>
          </CardContent>
        </Card>

        {/* Em Staking */}
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-muted-foreground">Em Staking</span>
              <Lock className="h-5 w-5 text-amber-500" />
            </div>
            <div className="text-2xl font-bold text-amber-600">{balance?.staked_display?.toFixed(2)}</div>
            <div className="text-xs text-muted-foreground">Rendendo 2% ao mês</div>
          </CardContent>
        </Card>
      </div>

      {/* Progresso de Check-ins para Saque */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold">Desafio de Atividade</h3>
              <p className="text-sm text-muted-foreground">
                Complete {balance?.checkins_required} check-ins no mês para habilitar o saque
              </p>
            </div>
            <Badge variant={balance?.can_withdraw ? "default" : "secondary"}>
              {balance?.checkins_this_month}/{balance?.checkins_required} check-ins
            </Badge>
          </div>
          <Progress 
            value={(balance?.checkins_this_month / balance?.checkins_required) * 100} 
            className="h-3"
          />
          {!balance?.can_withdraw && balance?.withdraw_blocked_reason && (
            <p className="text-sm text-amber-600 mt-2 flex items-center gap-1">
              <AlertCircle className="h-4 w-4" />
              {balance.withdraw_blocked_reason}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Ações */}
      <div className="flex gap-3 mb-6">
        <Button 
          onClick={() => setShowStakingModal(true)}
          className="bg-amber-500 hover:bg-amber-600"
        >
          <Lock className="h-4 w-4 mr-2" />
          Fazer Staking (2% ao mês)
        </Button>
        <Button 
          onClick={() => setShowWithdrawModal(true)}
          disabled={!balance?.can_withdraw}
          variant={balance?.can_withdraw ? "default" : "outline"}
        >
          <Send className="h-4 w-4 mr-2" />
          Solicitar Saque
        </Button>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="transactions" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="transactions">
            <History className="h-4 w-4 mr-2" />
            Histórico
          </TabsTrigger>
          <TabsTrigger value="affiliates">
            <Users className="h-4 w-4 mr-2" />
            Afiliados
          </TabsTrigger>
          <TabsTrigger value="staking">
            <PiggyBank className="h-4 w-4 mr-2" />
            Staking
          </TabsTrigger>
          <TabsTrigger value="withdraws">
            <Send className="h-4 w-4 mr-2" />
            Saques
          </TabsTrigger>
        </TabsList>

        {/* Histórico de Transações */}
        <TabsContent value="transactions">
          <Card>
            <CardHeader>
              <CardTitle>Histórico de Transações</CardTitle>
              <CardDescription>Todas as movimentações da sua carteira</CardDescription>
            </CardHeader>
            <CardContent>
              {transactions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <History className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Nenhuma transação ainda</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {transactions.map((tx, index) => (
                    <div 
                      key={tx.id || index} 
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                    >
                      <div className="flex items-center gap-3">
                        {getTransactionIcon(tx.transaction_type)}
                        <div>
                          <p className="font-medium text-sm">{tx.description}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatDistanceToNow(new Date(tx.created_at), { 
                              addSuffix: true, 
                              locale: ptBR 
                            })}
                          </p>
                        </div>
                      </div>
                      <div className={`font-bold ${
                        tx.amount_display >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {tx.amount_display >= 0 ? '+' : ''}{tx.amount_display?.toFixed(2)} AWA
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Afiliados */}
        <TabsContent value="affiliates">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-purple-600" />
                Programa de Afiliados
              </CardTitle>
              <CardDescription>
                Ganhe <span className="text-purple-600 font-bold">40%</span> de comissão em AWA Sacável quando seus indicados assinarem o Premium!
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Link de Convite */}
              <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                <Label className="text-sm font-medium mb-2 block">Seu Link de Convite</Label>
                <div className="flex gap-2">
                  <Input 
                    value={affiliateStats?.referral_link || "Carregando..."} 
                    readOnly 
                    className="bg-white"
                  />
                  <Button variant="outline" onClick={copyReferralLink}>
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button onClick={shareReferralLink}>
                    <Share2 className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Código: <span className="font-mono font-bold">{affiliateStats?.referral_code}</span>
                </p>
              </div>

              {/* Estatísticas */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-muted rounded-lg">
                  <p className="text-2xl font-bold">{affiliateStats?.total_referrals || 0}</p>
                  <p className="text-xs text-muted-foreground">Total de Indicados</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{affiliateStats?.converted_referrals || 0}</p>
                  <p className="text-xs text-muted-foreground">Convertidos (Premium)</p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <p className="text-2xl font-bold text-purple-600">{affiliateStats?.total_commission_awa?.toFixed(2) || '0.00'}</p>
                  <p className="text-xs text-muted-foreground">AWA Ganhos</p>
                </div>
                <div className="text-center p-4 bg-amber-50 rounded-lg">
                  <p className="text-2xl font-bold text-amber-600">R$ {affiliateStats?.total_commission_brl?.toFixed(2) || '0.00'}</p>
                  <p className="text-xs text-muted-foreground">Total em R$</p>
                </div>
              </div>

              {/* Lista de Indicados */}
              <div>
                <h4 className="font-semibold mb-3">Seus Indicados</h4>
                {affiliateReferrals.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>Você ainda não indicou ninguém</p>
                    <p className="text-sm">Compartilhe seu link e comece a ganhar!</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {affiliateReferrals.map((ref, index) => (
                      <div 
                        key={ref.id || index}
                        className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                      >
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={resolveImageUrl(ref.user_avatar)} />
                            <AvatarFallback>{ref.user_name?.[0] || 'U'}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{ref.user_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {formatDistanceToNow(new Date(ref.registered_at), { 
                                addSuffix: true, 
                                locale: ptBR 
                              })}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          {ref.converted ? (
                            <div>
                              <Badge className="bg-green-500">
                                <Crown className="h-3 w-3 mr-1" />
                                Premium
                              </Badge>
                              <p className="text-xs text-green-600 mt-1">
                                +R$ {ref.commission_earned?.toFixed(2)} em comissão
                              </p>
                            </div>
                          ) : (
                            <Badge variant="outline">Aguardando</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Staking */}
        <TabsContent value="staking">
          <Card>
            <CardHeader>
              <CardTitle>Posições de Staking</CardTitle>
              <CardDescription>AWA bloqueado rendendo 2% ao mês</CardDescription>
            </CardHeader>
            <CardContent>
              {stakingPositions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <PiggyBank className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Nenhum staking ativo</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setShowStakingModal(true)}
                  >
                    Criar Staking
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {stakingPositions.map((pos) => (
                    <div 
                      key={pos.id} 
                      className="p-4 rounded-lg border bg-amber-50/50"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-bold text-lg">{pos.amount_display?.toFixed(2)} AWA</span>
                        <Badge variant="outline" className="text-amber-600">
                          +{(pos.interest_rate * 100).toFixed(0)}% rendimento
                        </Badge>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        <p>Desbloqueio: {new Date(pos.unlocks_at).toLocaleDateString('pt-BR')}</p>
                        <p>Rendimento projetado: +{pos.projected_reward_display?.toFixed(2)} AWA</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Saques */}
        <TabsContent value="withdraws">
          <Card>
            <CardHeader>
              <CardTitle>Solicitações de Saque</CardTitle>
              <CardDescription>Histórico de saques via PIX</CardDescription>
            </CardHeader>
            <CardContent>
              {withdrawRequests.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Send className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Nenhuma solicitação de saque</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {withdrawRequests.map((req) => (
                    <div 
                      key={req.id} 
                      className="p-4 rounded-lg border"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-bold">R$ {req.amount_brl?.toFixed(2)}</span>
                        {getWithdrawStatusBadge(req.status)}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        <p>{req.amount_display?.toFixed(2)} AWA</p>
                        <p>PIX: {req.pix_key_type} - {req.pix_key}</p>
                        <p>{new Date(req.created_at).toLocaleDateString('pt-BR')}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal Staking */}
      <Dialog open={showStakingModal} onOpenChange={setShowStakingModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Criar Staking</DialogTitle>
            <DialogDescription>
              Bloqueie seus AWA por 30 dias e ganhe 2% de rendimento
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="staking-amount">Quantidade de AWA</Label>
              <Input
                id="staking-amount"
                type="number"
                placeholder="Ex: 100"
                value={stakingAmount}
                onChange={(e) => setStakingAmount(e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Disponível: {(balance?.common_display + balance?.withdrawable_display)?.toFixed(2)} AWA
              </p>
            </div>
            {stakingAmount && parseFloat(stakingAmount) > 0 && (
              <div className="bg-amber-50 p-3 rounded-lg">
                <p className="text-sm">
                  <strong>Rendimento projetado:</strong> +{(parseFloat(stakingAmount) * 0.02).toFixed(2)} AWA
                </p>
                <p className="text-xs text-muted-foreground">
                  Após 30 dias você receberá {(parseFloat(stakingAmount) * 1.02).toFixed(2)} AWA
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStakingModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleStaking} disabled={actionLoading}>
              {actionLoading ? 'Processando...' : 'Confirmar Staking'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Saque */}
      <Dialog open={showWithdrawModal} onOpenChange={setShowWithdrawModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Solicitar Saque</DialogTitle>
            <DialogDescription>
              Mínimo: 1000 AWA (R$ 1,00). O pagamento será feito via PIX em até 48h.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="withdraw-amount">Quantidade de AWA</Label>
              <Input
                id="withdraw-amount"
                type="number"
                placeholder="Ex: 1000"
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Disponível: {balance?.withdrawable_display?.toFixed(2)} AWA Sacável
              </p>
            </div>
            <div>
              <Label htmlFor="pix-type">Tipo de Chave PIX</Label>
              <Select value={pixKeyType} onValueChange={setPixKeyType}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cpf">CPF</SelectItem>
                  <SelectItem value="email">E-mail</SelectItem>
                  <SelectItem value="phone">Telefone</SelectItem>
                  <SelectItem value="random">Chave Aleatória</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="pix-key">Chave PIX</Label>
              <Input
                id="pix-key"
                placeholder="Digite sua chave PIX"
                value={pixKey}
                onChange={(e) => setPixKey(e.target.value)}
              />
            </div>
            {withdrawAmount && parseFloat(withdrawAmount) >= 1000 && (
              <div className="bg-green-50 p-3 rounded-lg">
                <p className="text-sm">
                  <strong>Valor do saque:</strong> R$ {(parseFloat(withdrawAmount) / 1000).toFixed(2)}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWithdrawModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleWithdraw} disabled={actionLoading}>
              {actionLoading ? 'Processando...' : 'Solicitar Saque'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
